import React, { useState } from 'react';
import { db, doc, getDoc, query, collection, where, getDocs } from '../firebase.js';

const UserTest = () => {
  const [userIdInput, setUserIdInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testType, setTestType] = useState(''); // 'id' or 'username'

  /**
   * Get user by ID
   * @param {string} userId - The user ID to look up
   * @returns {Promise<Object|null>} User data or null if not found
   */
  const getUserById = async (userId) => {
    if (!userId) return null;
    
    try {
      console.log(`Looking up user by ID: ${userId}`);
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        console.log("✅ USER FOUND by ID:", docSnap.id, docSnap.data());
        return { id: docSnap.id, ...docSnap.data() };
      }
      
      console.log(`❌ No user found with ID: ${userId}`);
      return null;
    } catch (error) {
      console.error('Error getting user by ID:', error);
      throw error;
    }
  };

  /**
   * Get user by username
   * @param {string} username - The username to look up
   * @returns {Promise<Object|null>} User data or null if not found
   */
  const getUserByUsername = async (username) => {
    if (!username) return null;
    
    try {
      const lowercaseUsername = username.toLowerCase();
      console.log(`🔍 Looking up user by username: ${username} (lowercase: ${lowercaseUsername})`);
      
      // Create a reference to the users collection
      const usersRef = collection(db, 'users');
      
      // Create a query against the "usernameLower" field
      const q = query(
        usersRef, 
        where('usernameLower', '==', lowercaseUsername)
      );
      
      // Execute the query
      const querySnapshot = await getDocs(q);
      
      // Check if any documents were found
      if (!querySnapshot.empty) {
        const docData = querySnapshot.docs[0];
        console.log("✅ USER FOUND by username:", docData.id, docData.data());
        return { id: docData.id, ...docData.data() };
      }
      
      // Log if user was not found
      console.log(`❌ No user found with username: ${username} (lowercase: ${lowercaseUsername})`);
      return null;
    } catch (error) {
      console.error('❌ Error getting user by username:', error);
      throw error;
    }
  };

  const handleTestById = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setTestType('id');
    
    try {
      const userData = await getUserById(userIdInput);
      setResult(userData);
    } catch (err) {
      setError(err.message || 'Error testing user ID');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestByUsername = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setTestType('username');
    
    try {
      const userData = await getUserByUsername(usernameInput);
      setResult(userData);
    } catch (err) {
      setError(err.message || 'Error testing username');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-test-container" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h1>User Fetch Diagnostic Tool</h1>
      
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem' }}>
        <div style={{ flex: 1 }}>
          <h2>Test by User ID</h2>
          <form onSubmit={handleTestById}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="userId" style={{ display: 'block', marginBottom: '0.5rem' }}>
                User ID:
              </label>
              <input
                id="userId"
                type="text"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc' 
                }}
                placeholder="Enter user ID"
              />
            </div>
            <button 
              type="submit" 
              disabled={loading || !userIdInput}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                opacity: loading || !userIdInput ? 0.7 : 1
              }}
            >
              {loading && testType === 'id' ? 'Testing...' : 'Test by ID'}
            </button>
          </form>
        </div>
        
        <div style={{ flex: 1 }}>
          <h2>Test by Username</h2>
          <form onSubmit={handleTestByUsername}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="username" style={{ display: 'block', marginBottom: '0.5rem' }}>
                Username:
              </label>
              <input
                id="username"
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc' 
                }}
                placeholder="Enter username"
              />
            </div>
            <button 
              type="submit" 
              disabled={loading || !usernameInput}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                opacity: loading || !usernameInput ? 0.7 : 1
              }}
            >
              {loading && testType === 'username' ? 'Testing...' : 'Test by Username'}
            </button>
          </form>
        </div>
      </div>
      
      {loading && (
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
          <p>Testing... Please check console for detailed logs.</p>
        </div>
      )}
      
      {error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#ffebee', 
          color: '#c62828',
          borderRadius: '4px',
          marginBottom: '1rem' 
        }}>
          <h3>Error:</h3>
          <p>{error}</p>
        </div>
      )}
      
      {result === null && !loading && !error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#fff3e0', 
          borderRadius: '4px',
          marginBottom: '1rem' 
        }}>
          <p>Enter a user ID or username and click Test to start.</p>
        </div>
      )}
      
      {result === null && !loading && testType && !error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#ffebee', 
          borderRadius: '4px',
          marginBottom: '1rem' 
        }}>
          <h3>No User Found</h3>
          <p>The user with the specified {testType === 'id' ? 'ID' : 'username'} was not found in the database.</p>
          <p>Check console for detailed logs.</p>
        </div>
      )}
      
      {result && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#e8f5e9', 
          borderRadius: '4px',
          marginBottom: '1rem' 
        }}>
          <h3>User Found!</h3>
          <div style={{ 
            backgroundColor: '#fff', 
            padding: '1rem',
            borderRadius: '4px',
            border: '1px solid #c8e6c9',
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            <pre>{JSON.stringify(result, null, 2)}</pre>
          </div>
        </div>
      )}
      
      <div style={{ 
        backgroundColor: '#e3f2fd', 
        padding: '1rem',
        borderRadius: '4px',
        marginTop: '2rem'
      }}>
        <h3>Debugging Tips:</h3>
        <ul>
          <li>Check console logs for detailed query information</li>
          <li>Verify that your Firebase configuration points to the correct project</li>
          <li>Ensure your security rules allow reading from the &apos;users&apos; collection</li>
          <li>Check if you&apos;re using Firebase emulators vs. production</li>
          <li>Verify that the user ID or username exists in your database</li>
        </ul>
      </div>
    </div>
  );
};

export default UserTest; 